import { FC, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";

import { useGetExternalConnectionQuery } from "@/App/api/externalConnections";
import { ContentPanel, PageWrapper } from "@/App/components";
import { SearchParamKeys } from "@/App/constants/constants";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";


export const ConnectionPage: FC = () => {
  const { t: translate } = useTranslation();
  const { connectionId } = useParams();
  const [searchParams] = useSearchParams();

  const sanitizedLocation = useMemo(() => {
    const clientQueryParam = searchParams.get(SearchParamKeys.EXPERIENCE_QUERY_IDENTIFIER);
    if (clientQueryParam) {
      return `${location.protocol}//${location.host}?${SearchParamKeys.EXPERIENCE_QUERY_IDENTIFIER}=${clientQueryParam}`;
    } else {
      return `${location.protocol}//${location.host}`;
    }
  }, []);

  const apiParams = {
    id: +connectionId,
    location: sanitizedLocation,
  };
  const isEmulating = sessionStorage.getItem("isEmulation");

  const { data: connectionData, isLoading } = useGetExternalConnectionQuery({ ...apiParams }, !!connectionId && !isEmulating);
  const formRef = useRef<HTMLFormElement>();

  useEffect(() => {
    if (!!connectionData && connectionData?.saml_response && connectionData?.destination) {
      logEvent(ANALYTIC_EVENTS.EXTERNAL_CONNECTION, { path: location });
      formRef?.current?.submit?.();
    }
  }, [connectionData, formRef]);

  return (
    <PageWrapper>
      <ContentPanel>
        <LoadingGroup loading={isLoading}>
          <LoadingGroup.Placeholder
            fallback={
              <Shelf>
                <Shelf.Spacer />
                <Spinner size="large" />
                <Shelf.Spacer />
              </Shelf>
            }
          >
            <Shelf>
              <Shelf.Spacer />
              <Paragraph testId="redirecting-to" textAlign="center">
                {`${translate("CONNECTION_PAGE.redirectingTo")} ${connectionData?.destination}`}
              </Paragraph>
              <Shelf.Spacer />
            </Shelf>
          </LoadingGroup.Placeholder>
        </LoadingGroup>
        <form ref={formRef} method="Post" action={connectionData?.destination}>
          <input type="hidden" name="SAMLResponse" value={btoa(connectionData?.saml_response)} />
          <input type="hidden" name="RelayState" value={connectionData?.relay_state} />
        </form>
      </ContentPanel>
    </PageWrapper>
  );
};
